import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { mixed, object } from 'yup'
import { FormInitializerReturnType, ValidationMessageKeys } from 'src/Configuration/validation'
import { ExportChargingCardFormData } from 'src/Libs/e-mobility/emobility-charging-cards'
import { isValid, isBefore, isAfter, isEqual } from '@e3dc-react/shell/Libs/date'

/**
 * Initializes the form for exporting an e-mobility charging card.
 * @returns initialValues and validationSchema for ExportChargingCardFormData form
 */
export const useExportEmobilityChargingCardEditorFormInitializer = (): FormInitializerReturnType<
  ExportChargingCardFormData,
  ExportChargingCardFormData
> => {
  const { t } = useCustomTranslation()

  const initialValues = {
    month: new Date(),
    year: new Date(),
    from: new Date(),
    to: new Date(),
  }

  const validationSchema = object({
    month: mixed<Date>()
      .test('isDate', t(ValidationMessageKeys.format), value => {
        if (!value) return false
        return isValid(value instanceof Date ? value : new Date(value))
      })
      .required()
      .test('isBeforeDate', t('emobility.chargingCards.export.validMonth'), (value?: Date) => {
        return value ? isBefore(value, new Date()) : false
      }),
    year: mixed<Date>()
      .test('is-date', t(ValidationMessageKeys.format), value => {
        if (!value) return false
        return isValid(value instanceof Date ? value : new Date(value))
      })
      .nullable()
      .test('isBeforeDate', t('emobility.chargingCards.export.validYear'), (value?: Date | null) => {
        return value ? isBefore(value, new Date()) : true
      })
      .required(),
    from: mixed<Date>()
      .test('is-date', t(ValidationMessageKeys.format), value => {
        if (!value) return false
        return isValid(value instanceof Date ? value : new Date(value))
      })
      .nullable()
      .test('isBeforeDate', t('emobility.chargingCards.export.validDateRange'), (value?: Date | null) => {
        return value ? isBefore(value, new Date()) : true
      })
      .required(),
    to: mixed<Date>()
      .test('is-date', t(ValidationMessageKeys.format), value => {
        if (!value) return false
        return isValid(value instanceof Date ? value : new Date(value))
      })
      .nullable()
      .test('isBeforeDate', t('emobility.chargingCards.export.validDateRange'), (value?: Date | null) => {
        return value ? isBefore(value, new Date()) : true
      })
      .when('from', ([fromDate], schema) => {
        const fromDateAsDate = fromDate instanceof Date ? fromDate : new Date(fromDate)
        return isValid(fromDateAsDate)
          ? schema.test('isAfterMinDate', t('emobility.chargingCards.export.validEndDate'), (value?: Date | null) => {
              return value ? isEqual(value, fromDateAsDate) || isAfter(value, fromDateAsDate) : true
            })
          : schema
      })
      .required(),
  })
  return { validationSchema, initialValues }
}
